import { useCallback, useEffect, useRef, useState } from 'react';
/* ⛔⛤ ~~Paperclip~~ — the glyph it drew was struck at Pete's request; the import
   went with it, because an unused import is a build failure, not a loose end. */
import { Eye, Loader2, PenLine, Printer, Trash2, TruckIcon } from 'lucide-react';
import {
    ApiCallError, getAppointmentPaperworkDossier, voidDocumentApi, flattenDossierDocuments,
    uploadDocument,
    type DocumentDto, type PreviewTarget,
} from '@/api/client';
import { DocumentPreview } from '@/components/DocumentPreview';
import { DocumentThumb } from '@/components/DocumentThumb';
import { FileDropZone } from '@/components/FileDropZone';
import { ConfirmDialog } from '@/components/ConfirmDialog';
/* ⛔ THE SAME PAYLOAD BUILDER THE TRAYS USE. A second `setData` call with the
   same MIME type and a slightly different shape is how two piles end up
   disagreeing about what a drag carries — see `lib/dnd.ts`. */
import { setPaperworkDrag } from '@/lib/dnd';

/**
 * A LOAD'S PAPERWORK, COMPACT, WHEREVER SOMEBODY IS ALREADY STANDING.
 *
 * ---- ⭐ WHY IT EXISTS -------------------------------------------------------
 *
 * Pete, 2026-08-25: *"maybe on the Edit Screen, you list the paperwork like you do in
 * the right column with the option to preview and delete?"*
 *
 * ⭐ THE POINT IS THAT EDITING A LOAD AND CHECKING ITS PAPERWORK ARE THE SAME ERRAND.
 * A scheduler correcting a PO or a container number is very often looking at the BOL
 * while they do it; making them close the editor, right-click the chip and open another
 * dialog turns one job into three.
 *
 * ---- ⚠️ THIS IS A SECOND RENDERING OF THE SAME FACTS, DELIBERATELY NARROW ---
 *
 * ⛔ IT IS NOT A SECOND SOURCE OF THEM. It reads the SAME dossier endpoint as
 * `AppointmentDocumentsDialog` — including paperwork filed against the ARRIVAL, which
 * is the subset bug that dialog shipped with — and it voids through the same API. What
 * differs is only how much is drawn.
 *
 * ⚠️ AND IT DELIBERATELY DOES LESS. No signing, no signed/original pairing, no removed
 * history. Those belong in the full dialog; duplicating them here would be two places
 * to change when #110's rules move, and the copy that got missed would quietly disagree
 * about which document a carrier was actually given.
 *
 * ⛔ SO IT SAYS SO ON SCREEN when there is more to see, rather than presenting itself
 * as the whole record.
 */

const KB = 1024;
function sizeOf(bytes: number): string {
    if (bytes < KB) return `${bytes} B`;
    if (bytes < KB * KB) return `${Math.round(bytes / KB)} KB`;
    return `${(bytes / (KB * KB)).toFixed(1)} MB`;
}

interface Props {
    appointmentId: string | null;
    /** ⭐ Shown beside "Paperwork" — a load number, or the driver's name. */
    label?: string | null;
    getToken: () => Promise<string>;
    /**
     * ⭐ #303 — WITH A FACILITY, THIS LIST CAN ALSO TAKE FILES. Pete, 2026-08-27,
     * on the Paperwork section of Edit A00000246: *"Need to be able to add
     * paperwork here too."*
     *
     * ⛔ OPTIONAL, AND ABSENT MEANS READ-ONLY RATHER THAN BROKEN. The upload
     * endpoint requires a facility code, and a caller that cannot supply one must
     * get a list it cannot add to — not a drop zone that fails at the server.
     */
    facilityCode?: string | null;
    /** Something was removed — the caller's counts and ticks are now stale. */
    onChanged?: () => void;
    /**
     * ⭐ PER-ROW, NOT A HEADER SHORTCUT (#245a). Where the documents are already
     * ON SCREEN, a button that guesses which one was meant is worse than one that
     * cannot: the scheduler can see the list, so let them point at a row.
     *
     * ⛔ The card and the right-click menu are the opposite case — the list is
     * hidden there, which is the whole reason `choosePaperworkAction` exists.
     */
    onSign?: (d: DocumentDto) => void;
    onPrint?: (d: DocumentDto) => void;
    /**
     * ---- ⭐⭐ HOW MANY DOCUMENTS THIS LIST IS HOLDING (2026-09-22) -----------
     *
     * > Pete: *"There also should be a number (like all the other panels) next to
     * > the refresh button."*
     *
     * ⛔ THE PANEL HEADER CANNOT COUNT THIS ITSELF. `PaperworkSliver` and
     * `ScanFolderPanel` are handed an `AttachedTarget` — the load being pointed
     * at — and the documents are fetched HERE. The header knows what it is looking
     * at and not what is in it.
     *   ** A COUNT BELONGS TO WHOEVER FETCHED THE ROWS. ANYWHERE ELSE IT IS A
     *      SECOND REQUEST OR A GUESS. **
     *
     * ⚠️ `null` IS SENT BEFORE THE FIRST LOAD, and it is not the same as `0`. The
     * header renders nothing for `null` — a `0` beside a list that has not been
     * read yet asserts this load has no paperwork, which is the one thing it must
     * not say by accident. Same rule HistoryPanel records for its own count.
     */
    onCount?: (n: number | null) => void;
}

export function PaperworkList({
    appointmentId, label, getToken, facilityCode, onChanged, onSign, onPrint, onCount,
}: Props) {
    const [docs, setDocs] = useState<DocumentDto[] | null>(null);

    /**
     * ⛔⛔ THE CALLBACK LIVES IN A REF, AND THE EFFECT DEPENDS ONLY ON `docs`.
     *
     * ⚠️ A parent that writes `onCount={n => setX(n)}` creates a NEW function on
     * every render. With `onCount` in the dependency array that is: effect runs →
     * parent setState → parent re-renders → new function identity → effect runs
     * again. An infinite loop, and it renders as a frozen tab rather than as an
     * error anybody can read.
     *   ** A CALLBACK PROP IN A DEPENDENCY ARRAY IS A RE-RENDER LOOP WAITING FOR A
     *      CALLER WHO DOES NOT MEMOISE. DO NOT REQUIRE STRANGERS TO BE CAREFUL. **
     *
     * ⛔ REPORTED FROM AN EFFECT, NEVER DURING RENDER. Calling a parent's setState
     * while this component is rendering is the "Cannot update a component while
     * rendering a different component" warning, and in a rail of five panels it is
     * a real re-entrancy bug rather than a lint nag.
     */
    const onCountRef = useRef(onCount);
    onCountRef.current = onCount;
    useEffect(() => {
        onCountRef.current?.(docs === null ? null : docs.length);
    }, [docs]);

    /**
     * ---- ⭐⭐ A CONVERTED PAIR IS ONE ROW, NOT TWO (migration 182) ----------
     *
     * Attaching a scanned JPEG produces a PDF **and keeps the image**, so a load
     * with four scans holds eight rows. ⛔ Showing both would double every list
     * and read as "why is everything here twice".
     *
     * ⭐ THE PDF IS WHAT A PERSON SEES — it is the printable, signable artefact.
     * The image is hidden behind it and used for one thing: the row's thumbnail,
     * which a PDF cannot produce without a renderer we deliberately did not ship
     * (§1AO).
     *
     * ⚠️ THE SOURCE IS HIDDEN, NOT DELETED. It is still on the load, still in the
     * dossier, and still what a dispute would be shown.
     */
    const sourceOf = new Map<string, DocumentDto>();
    const hidden = new Set<string>();
    for (const d of docs ?? []) {
        if (d.convertedFromDocumentId) {
            const src = (docs ?? []).find(x => x.documentId === d.convertedFromDocumentId);
            /* ⛔ ONLY HIDE A SOURCE WE ACTUALLY FOUND. If the pair is broken —
               the image voided, say — the PDF still shows and nothing vanishes
               silently. */
            if (src) { sourceOf.set(d.documentId, src); hidden.add(src.documentId); }
        }
    }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [viewing, setViewing] = useState<{ target: PreviewTarget; name: string } | null>(null);
    /** ⛔ The document awaiting confirmation. Never window.confirm. */
    const [removing, setRemoving] = useState<DocumentDto | null>(null);
    const [busy, setBusy] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);

    const load = useCallback(async () => {
        if (!appointmentId) return;
        setLoading(true);
        try {
            const r = await getAppointmentPaperworkDossier(await getToken(), appointmentId);
            /**
             * ⚠️ BOTH SETS, for the same reason the full dialog reads both: paperwork
             * dropped onto a DRIVER CARD is filed against the arrival, because a
             * walk-in has paperwork before he has an appointment. Reading only the
             * appointment's own rows would show a subset while looking complete.
             */
            // ⛔ DEDUPED — the two lists overlap. See flattenDossierDocuments.
            setDocs(flattenDossierDocuments(r).filter(d => !d.voidedUtc));
            setError(null);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not read this appointment\'s paperwork.');
        } finally {
            setLoading(false);
        }
    }, [appointmentId, getToken]);

    useEffect(() => { void load(); }, [load]);

    /**
     * ---- ⛔⛔ "Use scan folder…" MUST NOT APPEAR AT A SHAREPOINT SITE --------
     *
     * > Pete, 2026-09-10: *"Not sure why we have Use scan folder vs Add a file
     * > from this machine. Use scan folder opens up to the machine, same as add
     * > a file? Is it supposed to open up to sharepoint in Azure - where our
     * > scan drive is?"*
     *
     * ⛔ IT CANNOT, AND IT NEVER COULD. That link calls the browser's DIRECTORY
     * PICKER, which can only ever reach the local machine — a web page has no
     * path to a SharePoint drive, and those scans arrive server-side through
     * Graph instead. ⚠️ So at a SharePoint facility it offers a second, WRONG
     * source for the same pile.
     *
     * ⭐ THAT IS §1R.16 — "only one source is ever consulted" — broken in the
     * one place nobody had looked. `PaperworkSliver` and `ScanFolderPanel`
     * already obey it by rendering exclusively; this shared drop zone sits in
     * BOTH and had no idea which site it was on.
     *
     * ⚠️ IT FAILS OPEN, ON PURPOSE. An unreadable config leaves the link
     * showing — the old behaviour — because hiding a working control over a
     * failed config request is the worse of the two mistakes.
     */
    /* ⛔⛤ ~~const [localScans, setLocalScans]~~ AND ITS EFFECT — struck 2026-09-22.
       ⚠️ A WHOLE NETWORK ROUND TRIP FELL OUT OF THIS COMPONENT. The state existed
       for one reason: to tell FileDropZone whether to offer the directory picker,
       which it cannot use at a SharePoint site. With the prop gone there is no
       reader, so the `getDocumentConfig` call that fed it is a request whose answer
       nobody looks at.
         ** WHEN A PROP GOES, FOLLOW WHAT FED IT ALL THE WAY BACK. A DELETION THAT
            STOPS AT THE CALL SITE LEAVES A SERVER ROUND TRIP RUNNING FOR NOBODY. **
       ⭐ Its fail-open note is preserved where the rule now lives (ScanFolderPanel):
       an unreadable config left the control SHOWING, because hiding a working
       control over a failed config request is the worse of the two mistakes. */

    /**
     * ⛔ VOID, NOT DELETE — and the dialog says what that means. The bytes and the row
     * survive: "we had this and it was taken off the load on this date" and "we never
     * had this" are different sentences in a dispute.
     *
     * ⚠️ THE REASON IS CANNED because this control means one thing. The endpoint asks
     * for a reason since the question gets asked later; "removed while editing the
     * load" is the honest answer, and the server stamps who and when.
     */
    async function runRemove() {
        if (!removing) return;
        setBusy(true);
        try {
            await voidDocumentApi(
                await getToken(), removing.documentId,
                'Removed from the appointment while editing it.',
            );
            setRemoving(null);
            await load();
            onChanged?.();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not remove that document.');
            setRemoving(null);
        } finally {
            setBusy(false);
        }
    }

    if (!appointmentId) return null;

    /* ⛔ BOTH, OR NEITHER. The upload endpoint needs a facility code AND an
       appointment to file against; offering the control with either one missing
       would produce a drop that fails at the server — which is the shape of
       failure #288 was, and the one this whole area is trying to leave behind. */
    const canUpload = !!facilityCode && !!appointmentId;

    return (
        <div ref={anchorRef} className="space-y-1.5">
            {/**
              * ---- ⛔⚤ ~~THE NAME OF WHAT YOU ARE POINTING AT, ON THE LINE BELOW~~ --
              *     **SUPPRESSED PERMANENTLY 2026-09-20 (§0.3), AT PETE'S INSTRUCTION.**
              *
              * > *"the Paperwork · A00000320 · DD130 · 3:30 PM line needs to be
              * > suppressed permanently right now"*
              *
              * ⛔⛤ ~~<span>Paperwork{label ? ` · ${label}` : ''}</span>~~ — and the
              * ORIGINAL reasoning, kept because it was right on the day it was written:
              *
              * > Pete, 2026-09-10: *"The appointment number can move to line below
              * > Paperwork - Appt Number. or Driver Name, depending on what is hovered
              * > over."*  ⭐ *"THE LIST IS THE LOAD'S EITHER WAY. There is one dossier
              * > per appointment, so hovering the driver and hovering his chip show the
              * > same documents — only the label differs."*
              *
              * ⭐⭐ WHAT CHANGED IS THE HEADER ABOVE IT, NOT THE ARGUMENT. On 2026-09-10
              * the panel above said only *"Attached Paperwork"* and this line was the
              * ONLY thing naming the load. Today that header reads `Paperwork (TRAFFIX)`
              * or `Paperwork (Appt - A00000320)` in the shared rail format, so this line
              * is the second, longer copy of a fact stated ten pixels higher.
              *   ** A LABEL EARNS ITS PLACE AGAINST WHAT IS ALREADY ON SCREEN, NOT
              *      AGAINST WHAT WAS ON SCREEN WHEN IT WAS WRITTEN. ** (twice now)
              *
              * ⚠️ `label` IS STILL A PROP AND STILL PASSED. It is not dead: removing it
              * would touch three callers to delete an argument the next header change
              * would ask for back, and the panel title is built from the same focus.
              * ⛔ SO IT IS READ HERE, in the title attribute, rather than being deleted
              * and rediscovered — `noUnusedLocals` would name it otherwise, which is
              * exactly how `checkInEventLabel` was nearly lost yesterday.
              *
              * ⚠️ THE SPINNER SURVIVED THE LINE IT WAS SITTING ON. Its row now renders
              * only while loading — an always-present empty flex row leaves a gap above
              * the documents that reads as a missing element.
              */}
            {loading && (
                <div className="flex items-baseline justify-end" title={label ?? undefined}>
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                </div>
            )}

            {error && (
                <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                    {error}
                </div>
            )}

            {docs !== null && docs.length === 0 ? (
                /**
                 * ---- ⛔⛤ THE EMPTY-STATE PARAGRAPH IS STRUCK (2026-09-22) --------
                 *
                 * > Pete: *"Way too much going on in the paperwork section. I don't
                 * > want the comments here - remove 'Nothing filed on this load...'"*
                 *
                 * ⛔⛤ ~~Nothing filed on this load yet. Scans appear in the Scanned
                 * Paperwork tray in the arrivals column — drag one onto this load to
                 * file it, or add a file from this machine below.~~
                 *
                 * ⚠️ ITS ARGUMENT WAS GOOD AND IS OVERRULED BY DENSITY, WHICH IS A
                 * DIFFERENT THING FROM BEING WRONG. It existed because nothing in
                 * this app scans, so an unexplained empty list sends a scheduler
                 * hunting for a scan button that does not exist. ⭐ What has changed
                 * since it was written: the three source icons now sit in the header
                 * on every view, so "where does paperwork come from" is answered by a
                 * control rather than by a paragraph.
                 *   ** A SENTENCE THAT TEACHES A CONTROL IS OWED ONLY UNTIL THE
                 *      CONTROL IS VISIBLE. AFTER THAT IT IS FURNITURE. **
                 *
                 * ⛔ NOTHING RENDERS NOW, deliberately — not an empty bordered box.
                 * A dashed rectangle containing no words reads as a drop target,
                 * and the real drop target is directly below it.
                 */
                null
            ) : (
                <div className="divide-y rounded border">
                    {(docs ?? []).filter(d => !hidden.has(d.documentId)).map(d => (
                        /**
                          * ---- ⛔⛔ THE ROW IS A DRAG SOURCE (2026-09-10) -----------
                          *
                          * > Pete: *"Also, not letting me drag from attached paperwork
                          * > to anything else"*.
                          *
                          * ⛔ IT WAS A PLAIN `<div>`. Every OTHER pile in this rail
                          * drags — the SharePoint tray, the local folder tray — and
                          * this one, reached through the third icon, was a dead end:
                          * paperwork could go IN and never come back out. ⚠️ Which
                          * made "move it back from the Attached Paperwork to the
                          * Scanned Paperwork" impossible whatever the drop targets did,
                          * and the three icons read as broken for the second time.
                          *
                          * ⭐ IT CARRIES A `document` ITEM — the same payload the
                          * cloud tray's unfiled rows carry, so every existing drop
                          * handler already knows what to do with it: the cloud icon
                          * unfiles it, the local icon writes it to the scan folder, a
                          * load or a driver re-files it there.
                          *
                          * ⛔ ONE DOCUMENT, NOT A SELECTION. There are no tick boxes in
                          * this list, so there is no selection to honour — and
                          * inventing one here would be a second batching model beside
                          * `TrayCard`'s.
                          *
                          * ⚠️ A PURGED ROW DOES NOT DRAG. The record survives the file;
                          * dragging one would ask a drop handler to move bytes that no
                          * longer exist, and it would fail at the far end where nothing
                          * can explain it.
                          */
                        <div
                            key={d.documentId}
                            draggable={!d.purged}
                            onDragStart={(e) => {
                                if (d.purged) { e.preventDefault(); return; }
                                setPaperworkDrag(e.dataTransfer, [
                                    { kind: 'document', documentId: d.documentId, name: d.fileName },
                                ]);
                            }}
                            title={d.purged ? undefined : 'Drag to move this document'}
                            className={'flex items-start gap-1.5 px-2 py-1.5 '
                                + (d.purged ? '' : 'cursor-grab active:cursor-grabbing')}
                        >
                            {/**
                              * ⭐ THE THUMBNAIL COMES FROM THE IMAGE BEHIND THE PDF.
                              * ⛔ A PDF cannot produce one without the renderer we did
                              * not ship, so a converted row borrows its source's
                              * picture — which is the whole reason the image is kept.
                              */}
                            {d.signedFromDocumentId
                                ? <PenLine className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                : sourceOf.has(d.documentId)
                                    ? <DocumentThumb
                                        fileName={sourceOf.get(d.documentId)!.fileName}
                                        size={24}
                                      />
                                    : <DocumentThumb fileName={d.fileName} size={24} />}
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-xs" title={d.fileName}>{d.fileName}</div>
                                <div className="text-[10px] text-muted-foreground">
                                    {d.documentType ?? 'untyped'} · {sizeOf(d.bytes)}
                                    {d.signedFromDocumentId ? ' · signed copy' : ''}
                                    {/* ⚠️ SAID OUT LOUD. A scheduler who scanned a JPEG
                                        and sees a PDF should not have to wonder where it
                                        came from — and the original is still on the appointment,
                                        not gone. */}
                                    {sourceOf.has(d.documentId)
                                        ? ` · converted from ${sourceOf.get(d.documentId)!.fileName}`
                                        : ''}
                                </div>
                                {/* ⭐ Provenance, kept when the two lists are flattened. */}
                                {d.checkInId && (
                                    <div className="flex items-center gap-1 text-[10px] text-sky-700">
                                        <TruckIcon className="h-2.5 w-2.5" />
                                        Handed over at the gate
                                    </div>
                                )}
                            </div>
                            {/**
                              * ⭐ BOTH ALWAYS VISIBLE. Same decision as the paperwork
                              * tray: a control that only appears on hover is one a
                              * scheduler does not know exists, and is unreachable on a
                              * touch screen. The confirm dialog is what stops a bad
                              * removal, not concealment.
                              */}
                            <button
                                type="button"
                                draggable={false}
                                onClick={() => setViewing({
                                    target: { kind: 'document', documentId: d.documentId },
                                    name: d.fileName,
                                })}
                                disabled={d.purged}
                                title={d.purged ? 'The file has been purged; the record remains' : 'View'}
                                aria-label={`View ${d.fileName}`}
                                className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                                           hover:bg-primary/10 hover:text-foreground disabled:opacity-30"
                            >
                                <Eye className="h-3.5 w-3.5" />
                            </button>
                            {/**
                              * ⛔ SIGN IS OFFERED ON ORIGINALS ONLY. A signed copy is
                              * already a stamp; signing one would stamp a stamp, and
                              * `signedFromDocumentId` is exactly what tells them apart.
                              */}
                            {onSign && !d.signedFromDocumentId && !d.purged && (
                                <button
                                    type="button"
                                    draggable={false}
                                    onClick={() => onSign(d)}
                                    title="Sign this document"
                                    aria-label={`Sign ${d.fileName}`}
                                    className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                                               hover:bg-primary/10 hover:text-foreground"
                                >
                                    <PenLine className="h-3.5 w-3.5" />
                                </button>
                            )}
                            {/* ⚠️ Print acts on THIS row — no guessing needed, the
                                scheduler pointed at it. */}
                            {onPrint && !d.purged && (
                                <button
                                    type="button"
                                    draggable={false}
                                    onClick={() => onPrint(d)}
                                    title="Print this document"
                                    aria-label={`Print ${d.fileName}`}
                                    className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                                               hover:bg-primary/10 hover:text-foreground"
                                >
                                    <Printer className="h-3.5 w-3.5" />
                                </button>
                            )}
                            <button
                                type="button"
                                draggable={false}
                                onClick={() => setRemoving(d)}
                                title="Take this off the appointment"
                                aria-label={`Remove ${d.fileName}`}
                                className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                                           hover:bg-destructive/10 hover:text-destructive"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/**
              * ---- ⛔⚤ THE SENTENCE BECAME THE PAPERCLIP (Pete, 2026-09-20) ---------
              *
              * > *"'Signing and removed-document history are in Paperwork on the
              * > appointment.' needs to change into the attach symbol"* — and, asked
              * which symbol went where: *"the sentence under the attachment gets the
              * paperclip/attach icon, not the document icon."*
              *
              * ⛔⛤ ~~Signing and removed-document history are in **Paperwork** on the
              * appointment.~~ — SUPPRESSED PERMANENTLY.
              *
              * ⛔⛔ AND THE ARGUMENT THAT PUT IT THERE IS NOT WITHDRAWN, IT IS MOVED.
              * The original: *"IT ADMITS WHAT IT IS NOT SHOWING. Signing, the
              * original/signed-copy pairing and the removed-documents history live in
              * the full Paperwork dialog. A compact list that stayed silent about them
              * would read as the whole record, and somebody would conclude a signed
              * copy did not exist."* ⭐ THAT IS STILL TRUE, so the admission still has
              * to be reachable — it is the `title` and the `aria-label`, word for word.
              *   ** A SENTENCE MAY BECOME AN ICON. A FACT MAY NOT BECOME NOTHING. **
              *
              * ⚠️ AND AN ICON IS WEAKER THAN A SENTENCE HERE, WHICH IS WORTH SAYING
              * PLAINLY RATHER THAN PRETENDING THE SWAP IS FREE: a paperclip says
              * "there is more", not "signing lives elsewhere". Pete has the screen in
              * front of him and made the call on space; ⬜ if a scheduler ever asks
              * where signing went, this note is where the trade was recorded.
              *
              * ⛔ `aria-label` AS WELL AS `title`. A title attribute is not announced
              * reliably, and this element carries the ONLY statement of what the panel
              * omits — the one thing that must not be sighted-users-only.
              *
              * ⛔ `title` GOES ON THE WRAPPER, NOT ON THE ICON. Lucide's props are
              * `Omit<LucideProps,'ref'>` and carry no `title` — passing one is a
              * compile error, which is the good outcome: the alternative is an icon
              * whose explanation silently never renders.
              * ⚠️ AND THIS COMMENT SITS OUTSIDE `return (`-STYLE PARENS FOR A REASON.
              * A JSX COMMENT as the FIRST child of `cond && ( … )` makes two children
              * of a one-child slot — TS1005, the same shape that cost a build earlier
              * today. Comments about a JSX block go ABOVE the block, never inside its
              * opening paren.
              */}
            {/**
              * ---- ⛔⛤ THE PAPERCLIP IS STRUCK TOO (Pete, 2026-09-22) -------------
              *
              * > *"Also, remove the the Paperclip. Just keep 'Add a file from this
              * > machine - '"*
              *
              * ⚠️ AND THIS ONE COSTS A FACT, WHICH THE NOTE ABOVE IT PREDICTED IN
              * WRITING TWO DAYS AGO: *"A SENTENCE MAY BECOME AN ICON. A FACT MAY
              * NOT BECOME NOTHING."* The sentence became this icon on 2026-09-20;
              * today the icon becomes nothing, and the fact it carried —
              *
              *     signing, the original/signed-copy pairing and the
              *     removed-documents history live in the FULL Paperwork dialog,
              *     not in this compact list
              *
              * — is now stated NOWHERE on screen. ⛔ It is recorded here instead,
              * which is a worse place than a tooltip and a much worse place than a
              * sentence, and that is the trade being made.
              *   ** WHEN A FACT'S LAST CARRIER IS REMOVED, THE FACT DOES NOT MOVE
              *      SOMEWHERE ELSE BY ITSELF. **
              *
              * ⬜ IF A SCHEDULER EVER CONCLUDES A SIGNED COPY DOES NOT EXIST because
              * this list did not show it, this is the note to come back to. The cheap
              * fix at that point is a `title` on the list container, not the glyph
              * returning.
              */}

            <DocumentPreview
                anchor={anchorRef.current}
                target={viewing?.target ?? null}
                name={viewing?.name ?? ''}
                getToken={getToken}
                onClose={() => setViewing(null)}
                onDownload={() => { /* the full dialog owns downloading; nothing to do here */ }}
            />

            {/**
              * ⭐ #303 — ADD PAPERWORK FROM HERE TOO.
              *
              * ⛔ IT UPLOADS STRAIGHT ONTO THIS LOAD, not into the unfiled tray. A
              * scheduler who has the load open and picks a file has already
              * answered "which appointment"; routing it to the tray so somebody can
              * drag it back is a step that exists only because of how the code is
              * arranged.
              *
              * ⛔ AND IT IS THE SHARED CONTROL, NOT A FOURTH COPY. `FileDropZone`
              * owns the synchronous FileList snapshot that #288 turned on, the
              * success count Pete asked for, and the refusal to treat the app's own
              * drag as a file. The reasoning is written there.
              *
              * ⚠️ `onChanged` FIRES TOO. The caller's document counts and the ticks
              * on the appointment tile are now stale — the same notification a
              * removal sends, for the same reason.
              */}
            {canUpload && (
                <FileDropZone
                    getToken={getToken}
                    /* ⛔⛤ ~~scanFolder={localScans}~~ — the prop no longer exists.
                       ⚠️ ITS REASONING IS NOT WRONG, IT IS NO LONGER THIS FILE'S
                       PROBLEM: the browser's directory picker cannot reach a
                       SharePoint drive, so offering it at a SharePoint site
                       advertised a second source that did not exist. That rule now
                       lives where the control does — the header's local-folder icon
                       and `ScanFolderPanel`. */
                    label="Add a file from this machine — drop it here, or browse"
                    upload={(token, file) => uploadDocument(token, facilityCode as string, file, {
                        appointmentId: appointmentId as string,
                        source: 'Upload',
                    })}
                    onDone={async () => { await load(); onChanged?.(); }}
                />
            )}

            <ConfirmDialog
                open={removing !== null}
                title="Take this off the appointment?"
                message={
                    `"${removing?.fileName ?? ''}" stays stored and the record that it was `
                    + 'here survives — it just stops being attached to this appointment.\n\n'
                    + 'If it belongs on a different load, take it off here and drag it onto '
                    + 'the right one from the Paperwork tray.'
                }
                confirmLabel="Take it off"
                destructive
                busy={busy}
                onConfirm={() => void runRemove()}
                onCancel={() => setRemoving(null)}
            />
        </div>
    );
}
